import { Box, Container, Divider } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { WorkflowDefinition, WorkflowTemplateCategory } from "../../../../api/types/workflowTypes";
import VerticalNavigationMenuItem from "../../../common/navigation/VerticalNavigationMenuItem";
import VerticalNavigationSection from "../../../common/navigation/VerticalNavigationSection";
import VerticalNavigationWrapper from "../../../common/navigation/VerticalNavigationWrapper";
import WorkflowDefinitionsGrid from "./WorkflowDefinitionsGrid";

type StatusFilter = "all" | "active";

type Filter = { kind: "status"; value: StatusFilter } | { kind: "category"; value: WorkflowTemplateCategory };

const initialFilter: Filter = { kind: "status", value: "all" };

const filterDefinitions = (rows: WorkflowDefinition[], filter: Filter, searchTerm: string) => {
  const byFilter =
    filter.kind === "category"
      ? rows.filter((r) => r.category === filter.value)
      : filter.value === "active"
        ? rows.filter((r) => r.status === "Active")
        : rows;

  if (!searchTerm) {
    return byFilter;
  }

  return byFilter.filter(
    (r) =>
      r.name.toLocaleLowerCase().includes(searchTerm) ||
      r.description.toLocaleLowerCase().includes(searchTerm) ||
      r.category.toLocaleLowerCase().includes(searchTerm)
  );
};

const WorkflowDefinitions = () => {
  const [filter, setFilter] = useState<Filter>(initialFilter);
  const [searchTerm, setSearchTerm] = useState("");

  const [definitions, fetchError, { setData: setDefinitions }] = useFetch(adminApi.getWorkflowDefinitions);

  const rows = useMemo(() => definitions ?? [], [definitions]);

  const categories = useMemo(() => Array.from(new Set(rows.map((r) => r.category))).sort(), [rows]);

  const activeCount = useMemo(() => rows.filter((r) => r.status === "Active").length, [rows]);

  const countByCategory = useMemo(() => {
    const map = new Map<WorkflowTemplateCategory, number>();
    for (const r of rows) {
      map.set(r.category, (map.get(r.category) ?? 0) + 1);
    }
    return map;
  }, [rows]);

  const filteredRows = useMemo(() => filterDefinitions(rows, filter, searchTerm), [rows, filter, searchTerm]);

  const handleRowUpdate = useCallback(
    (updatedDefinition: WorkflowDefinition) => {
      setDefinitions((prev) => {
        if (!prev) {
          return prev;
        }

        return prev.map((def) => (def.workflowType === updatedDefinition.workflowType ? updatedDefinition : def));
      });
    },
    [setDefinitions]
  );

  if (fetchError) {
    logError(fetchError, "[WorkflowDefinitions] getWorkflowDefinitions");
    return <DataLoadingFailed />;
  }

  const loading = definitions === undefined;

  const isStatusSelected = (value: StatusFilter) => filter.kind === "status" && filter.value === value;
  const isCategorySelected = (value: WorkflowTemplateCategory) => filter.kind === "category" && filter.value === value;

  const handleSearch = (value: string) => {
    setSearchTerm(value.trim().toLocaleLowerCase());
  };

  return (
    <Box sx={{ display: "flex", flex: 1, height: "100%", minHeight: 0 }}>
      <VerticalNavigationWrapper>
        <VerticalNavigationSection title="Status">
          <VerticalNavigationMenuItem
            text="All"
            badgeCount={rows.length}
            selected={isStatusSelected("all")}
            onClick={() => setFilter({ kind: "status", value: "all" })}
          />
          <VerticalNavigationMenuItem
            text="Active"
            badgeCount={activeCount}
            selected={isStatusSelected("active")}
            onClick={() => setFilter({ kind: "status", value: "active" })}
          />
        </VerticalNavigationSection>
        <VerticalNavigationSection title="Categories">
          {categories.map((category) => (
            <VerticalNavigationMenuItem
              key={category}
              text={category}
              badgeCount={countByCategory.get(category) ?? 0}
              selected={isCategorySelected(category)}
              onClick={() => setFilter({ kind: "category", value: category })}
            />
          ))}
        </VerticalNavigationSection>
      </VerticalNavigationWrapper>
      <Divider flexItem orientation="vertical" />
      <Container sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, py: 2.5 }} maxWidth="xl">
        <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", mb: 2 }}>
          <RecordCounter records={filteredRows.length} totalRecords={rows.length} hide={loading} />
          <SearchField debounceTimeMs={300} onSearch={handleSearch} />
        </Box>

        <WorkflowDefinitionsGrid rows={filteredRows} onRowUpdate={handleRowUpdate} loading={loading} />
      </Container>
    </Box>
  );
};

export default WorkflowDefinitions;
